import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  Container,
  Row,
  Button,
  InputGroup,
  Col,
  Table,
  FormGroup,
  Modal,
  CardBody,
  Input,
} from 'reactstrap';
import Header from 'components/Headers/Header.js';
import { FaEdit, FaPlusCircle, FaTimesCircle } from 'react-icons/fa';
import api from 'services/api';
import { CircularProgress } from '@material-ui/core';
import Swal from 'sweetalert2';
import toast from 'react-hot-toast';

interface iIncentivoFiscal {
  id: number;
  nome: string;
  percentualReducaoIPI: number;
  percentualReducaoICMS: number;
  percentualReducaoPIS: number;
  percentualReducaoCOFINS: number;
  percentualReducaoISS: number;
  percentualReducaoST: number;
  percentualReducaoCBS: number;
  percentualReducaoIBS: number;
}

const IncentivoFiscal: React.FC = () => {
  const [lista, setLista] = useState([] as iIncentivoFiscal[]);
  const [registroEditing, setRegistroEditing] = useState(
    {} as iIncentivoFiscal,
  );
  const [loading, setLoading] = useState(false);

  const [formModalState, setFormModalState] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  async function carregarDados() {
    setLoading(true);
    const response = await api.get('/incentivo-fiscal');
    const listaUsar = response.data as iIncentivoFiscal[];
    setLista(listaUsar);
    setLoading(false);
  }

  useEffect(() => {
    carregarDados();
  }, []);

  async function handleDelete(registro: iIncentivoFiscal) {
    Swal.fire({
      title: `Deseja excluir o registro ${registro.nome}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Sim`,
      confirmButtonColor: '#d33',
      cancelButtonText: `Não`,
    }).then(async result => {
      if (result.isConfirmed) {
        await api.delete(`/incentivo-fiscal/${registro.id}`);

        Swal.close();
        toast.success('Registro excluído');
        carregarDados();
      }
    });
  }

  async function handleInsert() {
    setRegistroEditing({
      id: 0,
      nome: '',
      percentualReducaoCBS: 0,
      percentualReducaoCOFINS: 0,
      percentualReducaoIBS: 0,
      percentualReducaoICMS: 0,
      percentualReducaoIPI: 0,
      percentualReducaoISS: 0,
      percentualReducaoPIS: 0,
      percentualReducaoST: 0,
    } as iIncentivoFiscal);
    setFormModalState(!formModalState);
  }

  async function handleEdit(registro: iIncentivoFiscal) {
    setRegistroEditing({
      id: 0,
      nome: '',
      percentualReducaoCBS: 0,
      percentualReducaoCOFINS: 0,
      percentualReducaoIBS: 0,
      percentualReducaoICMS: 0,
      percentualReducaoIPI: 0,
      percentualReducaoISS: 0,
      percentualReducaoPIS: 0,
      percentualReducaoST: 0,
    } as iIncentivoFiscal);

    setIsSaving(false);
    setRegistroEditing(registro);
    setFormModalState(!formModalState);
  }

  async function handleSave() {
    const {
      id,
      nome,
      percentualReducaoCBS,
      percentualReducaoCOFINS,
      percentualReducaoIBS,
      percentualReducaoICMS,
      percentualReducaoIPI,
      percentualReducaoISS,
      percentualReducaoPIS,
      percentualReducaoST,
    } = registroEditing;

    if (!nome) {
      toast.error(
        'Todos os campos marcados com * são de preenchimento obrigatório!',
      );
      return;
    }

    setIsSaving(true);
    if (id) {
      await api.put(`/incentivo-fiscal/${id}`, {
        nome: String(nome).toUpperCase(),
        percentualReducaoCBS,
        percentualReducaoCOFINS,
        percentualReducaoIBS,
        percentualReducaoICMS,
        percentualReducaoIPI,
        percentualReducaoISS,
        percentualReducaoPIS,
        percentualReducaoST,
      });
    } else {
      await api.post(`/incentivo-fiscal`, {
        nome: String(nome).toUpperCase(),
        percentualReducaoCBS,
        percentualReducaoCOFINS,
        percentualReducaoIBS,
        percentualReducaoICMS,
        percentualReducaoIPI,
        percentualReducaoISS,
        percentualReducaoPIS,
        percentualReducaoST,
      });
    }

    toast.success('Registro concluído');
    setFormModalState(false);
    setRegistroEditing({} as iIncentivoFiscal);
    carregarDados();
    setIsSaving(false);
  }

  function renderTabelaConteudo() {
    if (loading) {
      return (
        <Row className="my-5">
          <Col className="text-center">
            <CircularProgress />
            <div className="mt-3 text-muted">Carregando lista...</div>
          </Col>
        </Row>
      );
    }

    if (!lista || lista.length === 0) {
      return (
        <Row className="my-5">
          <Col className="text-center text-muted">
            <i>Nenhum registro encontrado.</i>
          </Col>
        </Row>
      );
    }

    return (
      <div style={{ minHeight: 700, width: '100%' }}>
        <Table className="align-items-center table-flush mt-5" responsive>
          <thead className="thead-light">
            <tr>
              <th>#</th>
              <th>Nome</th>
              <th>ICMS (%)</th>
              <th>PIS (%)</th>
              <th>COFINS (%)</th>
              <th>IPI (%)</th>
              <th>ST (%)</th>
              <th>ISS (%)</th>
              <th>CBS (%)</th>
              <th>IBS (%)</th>
              <th> </th>
            </tr>
          </thead>
          <tbody>
            {lista.map((registro: iIncentivoFiscal, index: number) => {
              return (
                <tr key={`id_${index}`}>
                  <td>{registro.id} </td>
                  <td>{registro.nome} </td>
                  <td>{`${registro.percentualReducaoICMS}%`} </td>
                  <td>{`${registro.percentualReducaoPIS}%`} </td>
                  <td>{`${registro.percentualReducaoCOFINS}%`} </td>
                  <td>{`${registro.percentualReducaoIPI}%`} </td>
                  <td>{`${registro.percentualReducaoST}%`} </td>
                  <td>{`${registro.percentualReducaoISS}%`} </td>
                  <td>{`${registro.percentualReducaoCBS}%`} </td>
                  <td>{`${registro.percentualReducaoIBS}%`} </td>
                  <td>
                    <InputGroup>
                      <Button
                        size="sm"
                        className="btn-icon"
                        color="warning"
                        type="button"
                        onClick={() => {
                          handleEdit(registro);
                        }}
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        size="sm"
                        className="btn-icon float-right ml-1"
                        color="danger"
                        type="button"
                        onClick={() => {
                          handleDelete(registro);
                        }}
                      >
                        <FaTimesCircle />
                      </Button>
                    </InputGroup>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  }

  return (
    <>
      <Header showCards={false} />

      <Container className="mt--7" fluid>
        <Modal
          className="modal-dialog-centered"
          size="lg"
          isOpen={formModalState}
          toggle={() => setFormModalState(!formModalState)}
        >
          <div className="modal-body p-0">
            <Card className="bg-secondary shadow border-0">
              <CardBody className="px-lg-5 py-lg-5">
                <div className="text-center text-muted mb-4">
                  <small>Incentivo Fiscal</small>
                </div>

                <Row>
                  <Col sm="6">
                    <small>Nome</small>
                    <Input
                      placeholder="Descrição"
                      value={registroEditing.nome}
                      type="text"
                      disabled={registroEditing.id !== 0}
                      onChange={text =>
                        setRegistroEditing({
                          ...registroEditing,
                          nome: text.target.value,
                        })
                      }
                    />
                  </Col>
                </Row>

                <Row className="mt-2">
                  <Col sm="4">
                    <small>Redução ICMS (%)</small>
                    <Input
                      placeholder="ICMS (%)"
                      value={registroEditing.percentualReducaoICMS}
                      type="number"
                      onChange={text =>
                        setRegistroEditing({
                          ...registroEditing,
                          percentualReducaoICMS: Number(text.target.value),
                        })
                      }
                    />
                  </Col>
                  <Col sm="4">
                    <small>Redução PIS (%)</small>
                    <Input
                      placeholder="PIS (%)"
                      value={registroEditing.percentualReducaoPIS}
                      type="number"
                      onChange={text =>
                        setRegistroEditing({
                          ...registroEditing,
                          percentualReducaoPIS: Number(text.target.value),
                        })
                      }
                    />
                  </Col>
                  <Col sm="4">
                    <small>Redução COFINS (%)</small>
                    <Input
                      placeholder="COFINS (%)"
                      value={registroEditing.percentualReducaoCOFINS}
                      type="number"
                      onChange={text =>
                        setRegistroEditing({
                          ...registroEditing,
                          percentualReducaoCOFINS: Number(text.target.value),
                        })
                      }
                    />
                  </Col>
                </Row>

                <Row className="mt-2">
                  <Col sm="4">
                    <small>Redução IPI (%)</small>
                    <Input
                      placeholder="IPI (%)"
                      value={registroEditing.percentualReducaoIPI}
                      type="number"
                      onChange={text =>
                        setRegistroEditing({
                          ...registroEditing,
                          percentualReducaoIPI: Number(text.target.value),
                        })
                      }
                    />
                  </Col>
                  <Col sm="4">
                    <small>Redução ST (%)</small>
                    <Input
                      placeholder="ST"
                      value={registroEditing.percentualReducaoST}
                      type="number"
                      onChange={text =>
                        setRegistroEditing({
                          ...registroEditing,
                          percentualReducaoST: Number(text.target.value),
                        })
                      }
                    />
                  </Col>
                  <Col sm="4">
                    <small>Redução ISS (%)</small>
                    <Input
                      placeholder="ISS (%)"
                      value={registroEditing.percentualReducaoISS}
                      type="number"
                      onChange={text =>
                        setRegistroEditing({
                          ...registroEditing,
                          percentualReducaoISS: Number(text.target.value),
                        })
                      }
                    />
                  </Col>
                </Row>

                <Row className="mt-2">
                  <Col sm="4">
                    <small>Redução CBS (%)</small>
                    <Input
                      placeholder="CBS (%)"
                      value={registroEditing.percentualReducaoCBS}
                      type="number"
                      onChange={text =>
                        setRegistroEditing({
                          ...registroEditing,
                          percentualReducaoCBS: Number(text.target.value),
                        })
                      }
                    />
                  </Col>
                  <Col sm="4">
                    <small>Redução IBS (%)</small>
                    <Input
                      placeholder="IBS"
                      value={registroEditing.percentualReducaoIBS}
                      type="number"
                      onChange={text =>
                        setRegistroEditing({
                          ...registroEditing,
                          percentualReducaoIBS: Number(text.target.value),
                        })
                      }
                    />
                  </Col>
                </Row>

                <div className="text-center">
                  <Button
                    className="my-4"
                    color="primary"
                    type="button"
                    disabled={isSaving}
                    onClick={handleSave}
                  >
                    Salvar
                  </Button>
                </div>
              </CardBody>
            </Card>
          </div>
        </Modal>

        <Row>
          <Col>
            <FormGroup className="mb-4">
              <InputGroup>
                <Button
                  className="btn-icon btn-2"
                  color="primary"
                  type="button"
                  onClick={handleInsert}
                >
                  <FaPlusCircle />
                </Button>
              </InputGroup>
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0">
                <Row>
                  <Col sm="6">
                    <h3 className="mb-0">Incentivos Fiscais</h3>
                  </Col>
                </Row>
              </CardHeader>

              {renderTabelaConteudo()}
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default IncentivoFiscal;
